import {
  remarkBreaks,
  remarkGfm
} from "../../../chunk-ARRNLI4I.mjs";
import "../../../chunk-MSAROFZH.mjs";
import {
  Markdown
} from "../../../chunk-O3JOE2NW.mjs";
import "../../../chunk-XNL3XVS2.mjs";
import "../../../chunk-NEG2FAMT.mjs";
import {
  clsx_default
} from "../../../chunk-QJVDQ4DQ.mjs";
import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/ChatMarkdown/ChatMarkdown.tsx
import * as React from "react";
import { useShadowPortalContainer } from "../../../hooks/useShadowPortalContainer.mjs";
import { useComponentsTranslation } from "../../../i18n/useComponentsTranslation.mjs";
import { Text } from "../../primitive/index.mjs";
import { ChatImageLightbox } from "../../primitive/ChatImageView/ChatImageLightbox.mjs";
import { useLightboxTriggerProps } from "../../primitive/ChatImageView/lightboxHooks.mjs";
import { CopyButton } from "../../primitive/CopyButton/CopyButton.mjs";
import { ImageFrame } from "../../primitive/ImageFrame/ImageFrame.mjs";
import { Link } from "../../primitive/Link/Link.mjs";
import { ProductMentionChip } from "../Timeline/ContentChips/ProductMentionChip.mjs";
import { PRODUCT_MENTION_REGEX, resolveProductMention, unresolvedMentionText } from "../Timeline/ContentChips/productMentions.mjs";
import { ChatMarkdownContext } from "./context.mjs";
import { CopyableCodesContext, isCopyableCode } from "./copyableCode.mjs";
import { StreamChar } from "./StreamChar.mjs";
import { preprocessStreamingContent, rehypeCharacterStream, STREAM_REVEAL_MS, STREAM_SETTLE_MS, streamRevealProps, useStreamingAge } from "./streamingAnimation.mjs";
import { TableWrapper } from "./TableWrapper.mjs";
import { rehypeTextDirection } from "./textDirection.mjs";
import { chatUrlTransform, resolvedLinksUrlTransform } from "./urlTransform.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function encodeMentionUrlSegment(productKey) {
  return encodeURIComponent(productKey.trim()).replace(/\(/g, "%28").replace(/\)/g, "%29");
}
function decodeMentionProductKey(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
var CustomAnchor = React.memo(({ node, href, children, ...props }) => {
  const { animateState, isStreaming, productMap, resolveLink, storeDomain } = React.use(ChatMarkdownContext);
  const offset = node?.position?.start?.offset ?? href;
  const { age } = useStreamingAge(animateState, `a-${offset}`);
  const { className = "", style } = isStreaming ? streamRevealProps(age) : {};
  if (href?.startsWith("mention:")) {
    const productKey = decodeMentionProductKey(href.slice(8));
    const product = resolveProductMention(productMap, productKey);
    return product ? /* @__PURE__ */ jsx(Text, { variant: "body", as: "span", style, className, children: /* @__PURE__ */ jsx(
      ProductMentionChip,
      {
        product,
        storeDomain
      }
    ) }) : /* @__PURE__ */ jsx(Text, { variant: "body", as: "span", style, className, children: unresolvedMentionText(productKey) });
  }
  const resolved = resolveLink?.(href ?? "");
  if (resolveLink && !resolved) return /* @__PURE__ */ jsx("span", { style, className, children });
  return /* @__PURE__ */ jsx(
    Link,
    {
      variant: "accent",
      href: resolved?.href ?? href,
      external: resolved?.external,
      target: resolved ? resolved.target : "_blank",
      rel: resolved ? void 0 : "noopener noreferrer",
      className: `buyscout-chat-markdown-link ${className}`.trim(),
      style,
      ...props,
      onClick: (e) => {
        e.stopPropagation();
        props.onClick?.(e);
        resolved?.onClick?.(e);
      },
      children
    }
  );
});
CustomAnchor.displayName = "CustomAnchor";
var CustomImage = React.memo(({ node, src, alt }) => {
  const { animateState, isStreaming } = React.use(ChatMarkdownContext);
  const offset = node?.position?.start?.offset ?? src;
  const { age } = useStreamingAge(animateState, `img-${offset}`);
  const [failedSrc, setFailedSrc] = React.useState(null);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [container, handleThumbnailRef] = useShadowPortalContainer();
  const triggerProps = useLightboxTriggerProps(alt, () => setLightboxOpen(true));
  const failed = !src || failedSrc === src;
  const reveal = isStreaming ? streamRevealProps(age) : {};
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ImageFrame,
      {
        ref: handleThumbnailRef,
        src: src || void 0,
        alt: alt ?? "",
        fallback: "alt",
        loading: "lazy",
        className: failed ? alt ? void 0 : "buyscout-image-unavailable" : clsx_default("buyscout-image-container", reveal.className),
        imgClassName: "buyscout-image-thumbnail",
        style: failed ? void 0 : reveal.style,
        onError: () => setFailedSrc(src),
        ...!failed && {
          ...triggerProps,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setLightboxOpen(true);
          }
        }
      }
    ),
    !failed && /* @__PURE__ */ jsx(ChatImageLightbox, { open: lightboxOpen, onOpenChange: setLightboxOpen, url: src, alt, container })
  ] });
});
CustomImage.displayName = "CustomImage";
var CustomCode = React.memo(({ node: _node, children, ...props }) => {
  const { t } = useComponentsTranslation();
  const copyable = React.use(CopyableCodesContext);
  const { isStreaming } = React.use(ChatMarkdownContext);
  if (copyable && !isStreaming && typeof children === "string" && isCopyableCode(children)) {
    return /* @__PURE__ */ jsx(CopyButton, { value: children, label: children, "aria-label": t("copyButton.copyValue", { value: children }), className: "buyscout-chat-markdown-code" });
  }
  return /* @__PURE__ */ jsx("code", { ...props, children });
});
CustomCode.displayName = "CustomCode";
var MARKDOWN_COMPONENTS = {
  "stream-char": StreamChar,
  "a": CustomAnchor,
  "img": CustomImage,
  "code": CustomCode,
  "table": TableWrapper
};
var REMARK_PLUGINS = [remarkGfm, remarkBreaks];
var SETTLED_REHYPE_PLUGINS = [rehypeTextDirection];
var STREAMING_REHYPE_PLUGINS = [rehypeTextDirection, rehypeCharacterStream];
function parseMarkdown(content, revealing, linksResolved) {
  return Markdown({
    remarkPlugins: REMARK_PLUGINS,
    rehypePlugins: revealing ? STREAMING_REHYPE_PLUGINS : SETTLED_REHYPE_PLUGINS,
    components: MARKDOWN_COMPONENTS,
    urlTransform: linksResolved ? resolvedLinksUrlTransform : chatUrlTransform,
    children: content
  });
}
var SETTLED_CACHE_LIMIT = 200;
var settledMarkdown = /* @__PURE__ */ new Map();
function settledMarkdownFor(content, linksResolved) {
  const key = `${linksResolved ? "resolved" : "chat"}:${content}`;
  const cached = settledMarkdown.get(key);
  if (cached) {
    settledMarkdown.delete(key);
    settledMarkdown.set(key, cached);
    return cached;
  }
  const parsed = parseMarkdown(content, false, linksResolved);
  settledMarkdown.set(key, parsed);
  if (settledMarkdown.size > SETTLED_CACHE_LIMIT) settledMarkdown.delete(settledMarkdown.keys().next().value);
  return parsed;
}
function ChatMarkdownImpl({
  content,
  productMap,
  storeDomain,
  isStreaming,
  animateState,
  resolveLink
}) {
  const [wasStreaming, setWasStreaming] = React.useState(isStreaming);
  const [settling, setSettling] = React.useState(false);
  if (isStreaming !== wasStreaming) {
    setWasStreaming(isStreaming);
    setSettling(Boolean(wasStreaming && !isStreaming));
  }
  React.useEffect(() => {
    if (!settling) return;
    const timer = setTimeout(setSettling, STREAM_SETTLE_MS, false);
    return () => clearTimeout(timer);
  }, [settling]);
  const revealing = Boolean(isStreaming || settling);
  const [settledAt, setSettledAt] = React.useState(Number.NEGATIVE_INFINITY);
  React.useEffect(() => {
    const newest = animateState?.lastStartTime;
    if (!revealing || newest === void 0 || newest + STREAM_REVEAL_MS <= settledAt) return;
    const delay = Number.isFinite(settledAt) ? settledAt + STREAM_REVEAL_MS - Date.now() : 0;
    const timer = setTimeout(() => setSettledAt(Date.now()), Math.max(0, delay));
    return () => clearTimeout(timer);
  });
  const contextValue = React.useMemo(
    () => ({ animateState, isStreaming: revealing, settledAt, productMap, storeDomain, resolveLink }),
    [animateState, revealing, settledAt, productMap, storeDomain, resolveLink]
  );
  let processedContent = isStreaming ? preprocessStreamingContent(content) : content;
  processedContent = processedContent.replace(PRODUCT_MENTION_REGEX, (_match, productKey) => `[mention_chip](mention:${encodeMentionUrlSegment(productKey)})`);
  processedContent = processedContent.replace(
    /\[mention_chip\]\(mention:([^)]+)\)/g,
    (_match, rawKey) => `[mention_chip](mention:${encodeMentionUrlSegment(decodeMentionProductKey(rawKey))})`
  );
  const linksResolved = Boolean(resolveLink);
  const markdown = React.useMemo(
    () => revealing ? parseMarkdown(processedContent, true, linksResolved) : settledMarkdownFor(processedContent, linksResolved),
    [processedContent, revealing, linksResolved]
  );
  return /* @__PURE__ */ jsx(ChatMarkdownContext, { value: contextValue, children: markdown });
}
var ChatMarkdown = React.memo(ChatMarkdownImpl);
ChatMarkdown.displayName = "ChatMarkdown";
export {
  ChatMarkdown
};
//# sourceMappingURL=ChatMarkdown.mjs.map